/**
 * @description Página de recuperação de senha do sistema Fluxmed
 * @module Recover
 * @since Dezembro 2024
 *
 * Visual alinhado com RegularSignin e SignupPatient
 */

import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  Login as LoginIcon,
} from "@mui/icons-material";

import logo from "../../assets/fluxmed-logo-horizontal.png";
import background from "../../assets/background.png";

import { FormProvider, useForm, Controller } from "react-hook-form";
import { useFirebaseService } from "../../utils/useFirebase";
import { toast } from "react-toastify";
import { ProfileSearch } from "../../services/scim";
import { useScimData } from "../../utils/useScimData";
import { RecoverPassword } from "../../services/fhir";
import { useFormatter } from "../../utils/useFormatter";
import { useRegex } from "../../utils/useRegex";
import { useNavigate } from "react-router-dom";
import { FormInputValidationNumber } from "../signup/components/forminputinvalidationnumber";
import { currentFeatures } from "@/configs/Settings";

// Enum para controlar as etapas da recuperação
enum RecoverSteps {
  CONTACT_FORM = 0,
  VERIFICATION_CODE = 1,
  CONFIRMATION = 2,
}

interface IFormInput {
  credential: string;
  verificationCode: string;
}

const defaultValues = {
  credential: "",
  verificationCode: "",
};

export function Recover() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [page, setPage] = useState<RecoverSteps>(RecoverSteps.CONTACT_FORM);
  const [loading, setLoading] = useState(false);

  const methods = useForm<IFormInput>({ defaultValues });
  const { handleSubmit, control, getValues } = methods;
  const { sendVerificationCode, verifyCode } = useFirebaseService;

  /**
   * Verifica o código de verificação enviado por SMS
   */
  const handleVerifyCode = async () => {
    try {
      const verificationCode = getValues("verificationCode");
      await verifyCode(verificationCode);
      toast.success("Código validado com sucesso!");
      setPage(RecoverSteps.CONFIRMATION);
    } catch (error) {
      toast.error("Código inválido");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Processa o envio do formulário em cada etapa
   */
  const onSubmit = async (data: IFormInput) => {
    setLoading(true);

    if (page === RecoverSteps.CONTACT_FORM) {
      try {
        // Busca o usuário pelo CPF
        const Scim2UserResponse = await ProfileSearch(
          "application",
          data.credential?.replace(/[.-]/g, "")
        ).then((response) => response?.Resources);

        const phoneNumber = useScimData.getUser(Scim2UserResponse)?.phone;
        if (!phoneNumber) throw new Error("Usuário não encontrado");

        // Envia código de verificação para o telefone
        await sendVerificationCode(phoneNumber);
        toast.success("Código enviado para o telefone cadastrado!");
        setPage(RecoverSteps.VERIFICATION_CODE);
      } catch (err) {
        toast.error(`${err}`);
      } finally {
        setLoading(false);
      }
    }

    if (page === RecoverSteps.VERIFICATION_CODE) {
      try {
        await handleVerifyCode();
        // Envia solicitação de recuperação de senha
        await RecoverPassword(
          data.credential.replace(/[.-]/g, ""),
          "application"
        );
      } catch (err) {
        console.log("err", err);
      } finally {
        setLoading(false);
      }
    }
  };

  /**
   * Redireciona para a página de login
   */
  const handleGoToLogin = () => {
    navigate("/signin");
  };

  /**
   * Renderiza o formulário de CPF (etapa 1)
   */
  const renderContactForm = () => (
    <Box sx={{ py: 2 }}>
      <Box
        sx={{
          textAlign: "center",
          mb: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          src={logo}
          width={217}
          height="auto"
          alt="Logo"
          style={
            currentFeatures.defaultApp
              ? {
                  width: "250px",
                  height: "auto",
                }
              : {
                  height: "auto",
                  objectFit: "contain",
                  transform: "scale(2.5)",
                  margin: "0 auto 16px auto",
                }
          }
        />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          Problemas para entrar?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Insira o número de CPF associado à sua conta Fluxmed.
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Campo CPF */}
        <Controller
          name="credential"
          control={control}
          rules={{
            required: "CPF é obrigatório",
            pattern: {
              value: useRegex.cpf,
              message: "CPF inválido",
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              fullWidth
              label="CPF"
              value={value}
              onChange={(e) => onChange(useFormatter.formatCPF(e.target.value))}
              error={!!error}
              helperText={error?.message}
              placeholder="000.000.000-00"
              inputProps={{ maxLength: 14 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        {/* Botão Continuar */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={loading}
          sx={{
            bgcolor: "#00A884",
            py: 1.5,
            "&:hover": {
              bgcolor: "#008c6e",
            },
          }}
        >
          {loading ? "Enviando..." : "Continuar"}
        </Button>

        {/* Link para voltar ao login */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.5}
        >
          <Typography variant="body2" color="text.secondary">
            Lembra da sua senha?
          </Typography>
          <Button
            variant="text"
            onClick={handleGoToLogin}
            sx={{
              color: "#00A884",
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            Entrar
          </Button>
        </Stack>
      </Stack>
    </Box>
  );

  /**
   * Renderiza o formulário de código de verificação (etapa 2)
   */
  const renderVerificationCode = () => (
    <Box sx={{ py: 2 }}>
      <Box
        sx={{
          textAlign: "center",
          mb: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          src={logo}
          width={217}
          height="auto"
          alt="Logo"
          style={
            currentFeatures.defaultApp
              ? {
                  width: "250px",
                  height: "auto",
                }
              : {
                  height: "auto",
                  objectFit: "contain",
                  transform: "scale(2.5)",
                  margin: "0 auto 16px auto",
                }
          }
        />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          Verificação necessária
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Para continuar, conclua esta etapa de verificação. Enviamos um código
          para o telefone cadastrado. Insira-o abaixo.
        </Typography>
      </Box>

      <Stack spacing={3} alignItems="center">
        {/* Campo Código de Verificação */}
        <FormInputValidationNumber
          name="verificationCode"
          control={control}
          label=""
          pattern={[1 - 9]}
          autoFocus
        />

        {/* Botão Continuar */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={loading}
          sx={{
            bgcolor: "#00A884",
            py: 1.5,
            "&:hover": {
              bgcolor: "#008c6e",
            },
          }}
        >
          {loading ? "Verificando..." : "Continuar"}
        </Button>

        {/* Link para voltar ao login */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.5}
        >
          <Typography variant="body2" color="text.secondary">
            Lembra da sua senha?
          </Typography>
          <Button
            variant="text"
            onClick={handleGoToLogin}
            sx={{
              color: "#00A884",
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            Entrar
          </Button>
        </Stack>
      </Stack>
    </Box>
  );

  /**
   * Renderiza a mensagem de confirmação (etapa 3)
   */
  const renderConfirmation = () => (
    <Box sx={{ py: 4, textAlign: "center" }}>
      <CheckCircleIcon
        sx={{
          fontSize: isMobile ? 70 : 100,
          color: "#00A884",
          mb: 3,
          animation: "scaleIn 0.5s ease-in-out",
          "@keyframes scaleIn": {
            "0%": { transform: "scale(0)", opacity: 0 },
            "50%": { transform: "scale(1.1)" },
            "100%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      />

      <Typography
        variant={isMobile ? "h5" : "h4"}
        gutterBottom
        sx={{ fontWeight: 600, color: "#00A884", mb: 2 }}
      >
        Solicitação enviada!
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, px: 2 }}>
        O link para recuperação de senha foi enviado para o endereço de email
        associado à sua conta.
      </Typography>

      <Button
        variant="contained"
        size="large"
        startIcon={<LoginIcon />}
        onClick={handleGoToLogin}
        sx={{
          bgcolor: "#00A884",
          px: 4,
          py: 1.5,
          "&:hover": {
            bgcolor: "#008c6e",
          },
        }}
      >
        Acessar Fluxmed
      </Button>
    </Box>
  );

  /**
   * Renderiza o conteúdo da etapa atual
   */
  const renderStepContent = () => {
    switch (page) {
      case RecoverSteps.CONTACT_FORM:
        return renderContactForm();
      case RecoverSteps.VERIFICATION_CODE:
        return renderVerificationCode();
      case RecoverSteps.CONFIRMATION:
        return renderConfirmation();
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          pt: 15,
          pb: 2,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 2 : 4,
            borderRadius: "16px",
            width: "100%",
            backgroundColor: "#f0f0f0",
          }}
        >
          <FormProvider {...methods}>{renderStepContent()}</FormProvider>
        </Paper>
      </Container>
    </Box>
  );
}
